import { FC, useEffect, useState } from 'react';
import { Card } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useLazyQuery, gql, useMutation } from '@apollo/client';
import useSettings from 'src/hooks/useSettings';

export const GET_USERS = gql`
  query GetUsers($input: GetUsersInput) {
    Users(input: $input) {
      id
      email
      firstName
      lastName
      dateOfBirth {
        formatted
      }
    }
  }
`;

const subscriptionQuery = `
subscription {
  id
  created {
    formatted
  }
  updated {
    formatted
  }
  level
  trialDaysOnHeadsetConnect
  headsetConnectedTrialActivated
  subscriptionId
  status
  provider
  period
  price
  currency
  currentPeriodEnd {
    formatted
  }
  paidUntilDate {
    formatted
  }
  lineItems {
    id
    description
    currency
    quantity
    unitAmount
    productId
    priceId
    lineNumber
    lineType
    taxRate
  }
  nextInvoiceLineItemJSONSummary
  nextInvoiceLineItems {
    description
    secondaryDescription
    currency
    quantity
    unitAmount
    productId
    priceId
    lineNumber
    lineType
    taxRate
  }
  cancelAtPeriodEnd
  providerCustomerId
  providerPaymentMethodId
  orderId
  email
  couponAtSubscriptionCreation {
    couponCode
    couponDescription
    couponNumberOfDays
    discountAmount
  }
  couponCode
  couponNumberOfDays
  discountAmount
  invoices {
    id
    created {
      formatted
    }
    updated {
      formatted
    }
    provider
    providerCustomerId
    providerInvoiceId
    providerEventType
    providerStatus
    status
    amountDue
    amountPaid
    attemptCount
    attempted
    paid
    billingReason
    hostedInvoiceUrl
    periodStart {
      formatted
    }
    periodEnd {
      formatted
    }
    currency
    tax
    total
    couponCode
    discountAmount
  }
     contracts{
      minCharge
      minUsage
      periodsInTerm
      description
      discountPercent
      featureSkus
      renewalDate{
        formatted
      }
      canceledDate{
        formatted
      }
      cancelAtRenewalDate
      contractTerms
      acceptedAt{
        formatted 
      }
    }
}
`;

const planMemberAccessQuery = `
  id
  email
  ownerEmail
  ownerName
  membershipShortName
  status
  invitedAt {
    formatted
  }
  respondedAt {
    formatted
  }
  revokedAt {
    formatted
  }
`;

const membershipQuery = `
 
    id
    created {
      formatted
    }
    status
    level
    shortName
    canShareAccess
    planMembersMax
    planMembersActive
    planMembersActiveOrInvited
    planMemberAccess {
      ${planMemberAccessQuery}
    }
    startDate {
      formatted
    }
    endDate {
      formatted
    }
    featureBilling{
      productName
      priceId
      secondaryPriceId
      pricePerUnit
      currentPeriodValue
      basePrice
      periodEnd{
        formatted
      }
      numPeriodUpfront
      upfrontPeriodsRemaining
      noLimitPeriodsRemaining
      numPeriodNoLimit
      usageChargeDescription
      type
      ... on FeatureBillingByUsage{
        currentPeriodLimit
        priceStructure
      }
      ... on FeatureBillingBySeat{
        nextPeriodValue
        allowMinimumSeats
      }
    }
    ${subscriptionQuery}
  
`;

// Professional feature access (SEN-6027 / SEN-6028) is read by traversing
// `user -> organization -> activeProfessionalAccess` — there is no dedicated
// read resolver. DateTime fields are _Neo4jDateTime and must select { formatted }.
const professionalAccessFields = `
  id
  features
  assessmentAccessType
  totalAssessments
  inProgressAssessments
  usedAssessments
  availableAssessments
  headsetLimit
  grantsLinkedUserMembershipTrialDays
  assignedDate {
    formatted
  }
  expiryDate {
    formatted
  }
  created {
    formatted
  }
`;

const organizationProfessionalAccessQuery = `
  id
  organizationName
  organizationType
  organizationTypeOther
  activeProfessionalAccess {
    ${professionalAccessFields}
  }
  expiredProfessionalAccess {
    ${professionalAccessFields}
  }
`;

export const GET_USER = gql`
  query GetUser($email: String!) {
    User(email: $email) {
      id
      email
      firstName
      lastName
      nickname
      currentLevel
      expPoints
      dateOfBirth {
        formatted
      }
      unlockAccountAt {
        formatted
      }
      missions {
        id
        name
      }
      protocols {
        id
        name
      }
      features {
        id
        name
      }
      role {
        id
        name
      }
      lastUsedMacAddress
      headset {
        id
        created {
          formatted
        }
        firmwareVersion
        hardwareVersion
        mac
        modelNumber
        originalFirmwareVersion
        totalSessions
        totalAssessments
        firstSessionDate { formatted }
        lastSessionDate { formatted }
      }
      usedHeadsets {
        id
        created {
          formatted
        }
        firmwareVersion
        hardwareVersion
        mac
        modelNumber
        originalFirmwareVersion
        totalSessions
        totalAssessments
        firstSessionDate { formatted }
        lastSessionDate { formatted }
        userUsage
        warrantyEvents {
          id
          warrantyCreated {
            formatted
          }
          warranteedUnitShippedDate {
            formatted
          }
          serialNo
          reason
          subReason
          notes
          batchNo
          unitRetrieved
          haveSensorsBeenReplaced
          creditStatus
          order {
            id
            confirmationCode
          }
        }
      }
      recentGuestSessions {
        id
        start {
          formatted
        }
        state
      }
      expiredMemberships {
        ${membershipQuery}
      }
      membership {
        ${membershipQuery}
      }

      expiredProMemberships {
        ${membershipQuery}
      }
      proMembership {
        ${membershipQuery}
      }
      organization {
        ${organizationProfessionalAccessQuery}
      }
      orders {
        id
        email
        confirmationCode
        paymentProvider
        paymentIntentId
        status
        affiliateId
        fulfilmentStatus
        fulfilmentId
        fulfilmentAltId
        fulfilmentWarehouseCode
        amount
        refundAmount
        currency
        created {
          formatted
        }
        updated {
          formatted
        }
        shippedDate {
          formatted
        }
        refundDate {
          formatted
        }
        lineItems {
          id
          description
          currency
          quantity
          unitAmount
          refundAmount
          productId
          lineNumber
          lineType
          sku
          refundQuantity
          returnQuantity
        }
        shippingAddress {
          country
          city
          postal
          street
          unit
          firstName
          lastName
          phone
          province
          created {
            formatted
          }
          updated {
            formatted
          }
        }
        billingAddress {
          country
          city
          postal
          street
          unit
          firstName
          lastName
          phone
          province
          created {
            formatted
          }
          updated {
            formatted
          }
        }
        tracking {
          id
          courierName
          serviceName
          trackingNumber
          trackingUrl
        }
        country
        utmSource
        utmMedium
        utmCampaign
        utmTerm
        utmContent
        refundReason
        refundNotes
        couponCode
        warrantyEvents {
          id
          warrantyCreated {
            formatted
          }
          warranteedUnitShippedDate {
            formatted
          }
          serialNo
          reason
          subReason
          notes
          batchNo
          unitRetrieved
          haveSensorsBeenReplaced
          creditStatus
          order {
            id
            confirmationCode
          }
        }
      }
      metadata {
        id
        key
        value
      }
      membershipPlanMemberAccess {
        ${planMemberAccessQuery}
      }
      expiredMembershipPlanMemberAccess {
        ${planMemberAccessQuery}
      }
    }
  }
`;

interface UserInspectorProps {
  email: string;
}

const UserInspector = ({ email }: UserInspectorProps) => {
  const { settings } = useSettings();
  const [getUsers, { loading, data, error }] = useLazyQuery(GET_USER);

  useEffect(() => {
    getUsers({
      variables: { email: email }
    });
  }, []);

  useEffect(() => {
    if (data) {
      console.log('data', data);
    }
    if (error) {
      console.log('error', error);
    }
  }, [data, error]);

  return (
    <>
      {loading ? (
        <Card
          elevation={0}
          style={{
            height: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginTop: 222
          }}
        >
          <CircularProgress color="secondary" />
        </Card>
      ) : (
        <Card
          style={{
            height: '100%',
            overflow: 'auto',
            marginTop: 66,
            padding: 20
          }}
        >
          {data && <pre>{JSON.stringify(data, null, 2)}</pre>}
        </Card>
      )}
    </>
  );
};

export default UserInspector;
